// Market expansion M6 — read hook over get_market_coverage_overview().
// Anon + authenticated (RPC granted to both). Read-only, no PII, no
// writes. Soft-fails to the honest-empty view (never throws, never
// fabricates a modelled count or a decline). Shaping is the tested
// pure function from marketCoverage.ts.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  shapeMarketCoverage,
  EMPTY_MARKET_COVERAGE,
  type MarketCoverageOverview,
} from "./marketCoverage.ts";

export interface UseMarketCoverageState {
  readonly overview: MarketCoverageOverview;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
}

export function useMarketCoverage(): UseMarketCoverageState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [data, setData] = useState<MarketCoverageOverview>(
    EMPTY_MARKET_COVERAGE,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: rpcErr } = await (supabase.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: unknown; error: { message: string } | null }>)(
        "get_market_coverage_overview",
      );
      if (rpcErr || !res) {
        if (rpcErr) setError(rpcErr.message);
        setData(EMPTY_MARKET_COVERAGE);
      } else {
        setData(shapeMarketCoverage(res));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(EMPTY_MARKET_COVERAGE);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({ overview: data, loading, error, refetch }),
    [data, loading, error, refetch],
  );
}
